import type { LiveAgentSchedule } from "./schemas/live-agent-schedule.schema";

export const candidateLiveAgentTriggerEvents = [
  "recruitment.candidate.created",
  "recruitment.candidate.updated",
  "recruitment.candidate.deleted",
] as const;

export type CandidateLiveAgentTriggerEvent = (typeof candidateLiveAgentTriggerEvents)[number];

export const liveAgentIntervalTriggerEvent = "system.interval.elapsed" as const;

export const liveAgentTriggerEvents = [...candidateLiveAgentTriggerEvents, liveAgentIntervalTriggerEvent] as const;

export type LiveAgentTriggerEvent = (typeof liveAgentTriggerEvents)[number];

export const liveAgentTriggerDefinitions: Record<
  LiveAgentTriggerEvent,
  {
    group: string;
    label: string;
    description: string;
    defaultPrompt: string;
    recordType: string;
    recordSource: string;
    recordLabel: string;
  }
> = {
  "recruitment.candidate.created": {
    group: "Candidates",
    label: "Candidate created",
    description: "Runs when a candidate is added from any source.",
    defaultPrompt: "Review the new candidate and complete the appropriate next steps.",
    recordType: "candidate",
    recordSource: "candidate",
    recordLabel: "Candidate",
  },
  "recruitment.candidate.updated": {
    group: "Candidates",
    label: "Candidate updated",
    description: "Runs when candidate data changes.",
    defaultPrompt: "Review the candidate's latest information and complete any appropriate follow-up.",
    recordType: "candidate",
    recordSource: "candidate",
    recordLabel: "Candidate",
  },
  "recruitment.candidate.deleted": {
    group: "Candidates",
    label: "Candidate deleted",
    description: "Runs after a candidate is removed.",
    defaultPrompt: "Review the deletion event and complete any necessary cleanup or follow-up.",
    recordType: "candidate",
    recordSource: "candidate",
    recordLabel: "Candidate",
  },
  "system.interval.elapsed": {
    group: "Schedule",
    label: "Schedule",
    description: "Runs on a recurring schedule, with optional hours and days.",
    defaultPrompt: "Review the current workspace state and complete the appropriate scheduled work.",
    recordType: "interval",
    recordSource: "interval",
    recordLabel: "Scheduled occurrence",
  },
};

export function isLiveAgentTriggerEvent(event: string): event is LiveAgentTriggerEvent {
  return liveAgentTriggerEvents.includes(event as LiveAgentTriggerEvent);
}

export function getLiveAgentTriggerDefinition(event: LiveAgentTriggerEvent) {
  return liveAgentTriggerDefinitions[event];
}

export function isCandidateLiveAgentTriggerEvent(event: string): event is CandidateLiveAgentTriggerEvent {
  return candidateLiveAgentTriggerEvents.includes(event as CandidateLiveAgentTriggerEvent);
}

export type LiveAgentPlatformEventTrigger = {
  event: CandidateLiveAgentTriggerEvent;
  prompt: string;
};

export type LiveAgentIntervalTrigger = {
  event: typeof liveAgentIntervalTriggerEvent;
  prompt: string;
  schedule: LiveAgentSchedule;
};

export type LiveAgentEventTrigger = LiveAgentPlatformEventTrigger | LiveAgentIntervalTrigger;

export function isLiveAgentIntervalTrigger(trigger: LiveAgentEventTrigger): trigger is LiveAgentIntervalTrigger {
  return trigger.event === liveAgentIntervalTriggerEvent;
}

export function isLiveAgentPlatformEventTrigger(
  trigger: LiveAgentEventTrigger,
): trigger is LiveAgentPlatformEventTrigger {
  return trigger.event !== liveAgentIntervalTriggerEvent;
}
